import { supabase } from '../lib/supabaseClient';
import { materials } from '../constants/materials';
import { additionalOptions } from '../constants/options';
import { productTypes } from '../constants/materials';

interface CalculationRequest {
  material: string;
  thickness: number;
  width: number;
  height: number;
  quantity: number;
  processing?: {
    cutting?: boolean;
    bending?: boolean;
    printing?: boolean;
  };
}

interface CalculationResult {
  basePrice: number;
  processingPrice: number;
  totalPrice: number;
  unitPrice: number;
  details: {
    materialCost: number;
    cuttingCost?: number;
    bendingCost?: number;
    printingCost?: number;
  };
}

export class ChatbotCalculatorService {
  // UŻYWAMY DOKŁADNIE TEJ SAMEJ LOGIKI CO GŁÓWNY KALKULATOR!
  private getMaterialPrice(materialName: string, thickness: number): number {
    // Mapowanie nazw z chatbota na ID materiałów z constants/materials
    const materialMap: Record<string, string> = {
      'Plexi transparentna': 'plexi',
      'Plexi bezbarwna': 'plexi', 
      'Plexi bezbarwne': 'plexi',
      'Plexi mleczna': 'plexi_opal',
      'Plexi mleczne': 'plexi_opal',
      'Plexi opal': 'plexi_opal',
      'Plexi satynowa': 'plexi_satyna',
      'Plexi satynowe': 'plexi_satyna',
      'Plexi czarna': 'plexi_color',
      'Plexi kolorowa': 'plexi_color',
      'Plexi kolorowe': 'plexi_color',
      'PCV': 'pcv',
      'PCV spienione': 'pcv',
      'Dibond': 'dibond',
      'PETG': 'petg',
      'PET-G': 'petg',
      'HIPS': 'petg', // Fallback na PETG
      'Poliwęglan': 'poliweg',
      'Poliweg': 'poliweg'
    };

    const materialId = materialMap[materialName] || 'plexi';
    const material = materials.find(m => m.id === materialId);
    
    if (!material) {
      // Fallback na plexi bezbarwne
      return 30 * thickness; // 30 zł/m²/mm
    }

    // DOKŁADNIE JAK W GŁÓWNYM KALKULATORZE:
    
    // Dibond ma stałą cenę 80 zł/m² (nie mnożymy przez grubość)
    if (material.id === 'dibond') {
      return material.basePrice; // 80 zł/m²
    }
    
    // Plexi kolorowa - cena zależy od grubości (z tabeli)
    if (material.id === 'plexi_color' && material.pricePerThickness) {
      return material.pricePerThickness[thickness] || (50 * thickness);
    }
    
    // Pozostałe materiały - cena bazowa × grubość
    // plexi: 30 zł/m²/mm
    // plexi_satyna: 36 zł/m²/mm
    // plexi_opal: 33 zł/m²/mm
    // petg: 28 zł/m²/mm
    // poliweg: 40 zł/m²/mm
    // pcv: 12 zł/m²/mm
    return material.basePrice * thickness;
  }

  // Oblicz koszt grafiki/druku - używamy tej samej logiki co główny kalkulator
  private calculatePrintingCost(width: number, height: number, quantity: number): number {
    const area = (width * height) / 1000000; // m²
    const printingOption = additionalOptions.find(o => o.id === 'grafika');
    const printingRate = printingOption?.price || 75; // 75 zł za m²
    return area * printingRate * quantity;
  }

  // Główna metoda kalkulacji - IDENTYCZNA JAK W GŁÓWNYM KALKULATORZE
  calculate(request: CalculationRequest): CalculationResult {
    // Znajdujemy typ produktu (formatka) - chatbot domyślnie używa formatki
    const product = productTypes.find(p => p.id === 'formatka');
    const waste = product?.waste || 0.05; // 5% odpadu dla formatki
    const multiplier = product?.multiplier || 1.3; // Mnożnik 1.3 dla formatki
    
    // Oblicz powierzchnię w m²
    const area = (request.width * request.height) / 1000000;
    
    // Powierzchnia z odpadem - JAK W GŁÓWNYM KALKULATORZE
    const areaWithWaste = area * (1 + waste);
    
    // Pobierz cenę materiału za m²
    const materialPricePerM2 = this.getMaterialPrice(request.material, request.thickness);
    
    // Koszt materiału - JAK W GŁÓWNYM KALKULATORZE
    const materialCost = materialPricePerM2 * areaWithWaste;
    
    // Koszty obróbki
    let printingCost = 0;
    
    if (request.processing?.printing) {
      printingCost = this.calculatePrintingCost(request.width, request.height, request.quantity);
    }
    
    // DOKŁADNIE JAK W GŁÓWNYM KALKULATORZE:
    // Robocizna to 33.3% kosztu materiału
    const laborCost = materialCost * 0.333;
    
    // Cena przed marżą
    const costBeforeMargin = materialCost + laborCost;
    
    // Suma kosztów obróbki
    const processingPrice = printingCost;
    
    // Cena jednostkowa z mnożnikiem (marża produktu)
    const unitPrice = costBeforeMargin * multiplier + processingPrice;
    
    // Cena całkowita
    const totalPrice = unitPrice * request.quantity;
    
    // Minimalne zamówienie 0.1m² dla pojedynczej sztuki (jak w głównym kalkulatorze)
    let adjustedUnitPrice = unitPrice;
    let adjustedTotalPrice = totalPrice;
    
    if (request.quantity === 1 && area < 0.1) {
      const minSurfaceMultiplier = 0.1 / area;
      adjustedUnitPrice = unitPrice * minSurfaceMultiplier;
      adjustedTotalPrice = adjustedUnitPrice;
    }
    
    return {
      basePrice: materialCost,
      processingPrice,
      totalPrice: adjustedTotalPrice,
      unitPrice: adjustedUnitPrice,
      details: {
        materialCost,
        printingCost: printingCost > 0 ? printingCost : undefined,
      }
    };
  }

  // Formatuj wynik do czytelnej wiadomości - BEZ POKAZYWANIA LOGIKI!
  formatResult(result: CalculationResult, request: CalculationRequest): string {
    let message = `**📋 Twoja wycena:**\n\n`;
    
    // Podstawowe informacje o produkcie
    message += `**Produkt:** ${request.material} ${request.thickness}mm\n`;
    message += `**Wymiary:** ${request.width} × ${request.height} mm\n`;
    message += `**Ilość:** ${request.quantity} szt.\n\n`;
    
    // Tylko ceny końcowe - bez logiki!
    if (request.quantity > 1) {
      message += `💰 **Cena jednostkowa:** ${result.unitPrice.toFixed(2)} zł netto\n`;
      message += `💰 **Cena całkowita:** ${result.totalPrice.toFixed(2)} zł netto\n\n`;
    } else {
      message += `💰 **Cena:** ${result.totalPrice.toFixed(2)} zł netto\n\n`;
    }
    
    // Dodaj informacje o usługach wliczonych w cenę
    message += `✅ **W cenie:**\n`;
    message += `• Cięcie na wymiar\n`;
    message += `• Profesjonalne wykończenie krawędzi\n`;
    message += `• Ochronna folia zabezpieczająca\n\n`;
    
    // Informacje dodatkowe
    message += `📦 **Czas realizacji:** 3-5 dni roboczych\n`;
    message += `🚚 **Transport:** wyceniamy indywidualnie\n\n`;
    
    message += `_Do ceny należy doliczyć 23% VAT_`;
    
    return message;
  }

  // Metoda dla prostej odpowiedzi cenowej (jeszcze krótsza)
  formatQuickPrice(result: CalculationResult, request: CalculationRequest): string {
    let message = `💰 **Twoja cena:** ${result.totalPrice.toFixed(2)} zł netto\n\n`;
    
    message += `📦 ${request.material} ${request.thickness}mm\n`;
    message += `📐 ${request.width} × ${request.height} mm`;
    
    if (request.quantity > 1) {
      message += ` × ${request.quantity} szt.`;
    }
    
    message += `\n\n_Do ceny należy doliczyć 23% VAT_`;
    
    return message;
  }

  // Metoda pomocnicza do wyświetlania cennika
  getCatalogPrices(): string {
    return `**📋 CENNIK MATERIAŁÓW PlexiSystem:**

**PLEXI (PMMA):**
• Bezbarwne: 30 zł/m²/mm
• Satynowe: 36 zł/m²/mm  
• Mleczne/opal: 33 zł/m²/mm
• Kolorowe: 50-1500 zł/m² (zależnie od grubości)

**INNE MATERIAŁY:**
• PET-G: 28 zł/m²/mm
• Poliwęglan: 40 zł/m²/mm
• Dibond: 80 zł/m² (tylko 3mm)
• PCV spienione: 12 zł/m²/mm

**DODATKI:**
• Grafika jednostronna: 75 zł/m²
• Grafika dwustronna: 150 zł/m²
• Polerowanie krawędzi: 9 zł/mb
• Zawiasy/Zamek: 18 zł/szt
• LED standard: 30 zł/mb
• Karton: 8 zł/m²

_Ceny netto, do cen należy doliczyć 23% VAT_`;
  }
}

export const chatbotCalculator = new ChatbotCalculatorService();
